import { NoResultForFilter } from '@components/NoResultForFilter/NoResultForFilter';
import { Z_INDEX } from '@constants/z-index';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { useLibraryFavoritesSync } from '@lib/hooks/useFavoriteLibraries';
import { formatLibraryCardMeta } from '@lib/utils/libraryStats';
import { AppPage } from '@pages/AppPage';
import { LibraryFolderProvider } from '@stores/Library/LibraryFolder';
import { libraryFavorites } from '@stores/LibraryFavorites';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { trackingStore } from '@stores/Tracking';
import { useUIPreferences } from '@stores/UIPreferences';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import css from './LibrariesPage.module.scss';
import { AddMediaModal, AddMediaModalRef } from './components/AddMediaModal/AddMediaModal';
import { LibrariesNav } from './components/LibrariesNav';
import { LibrariesTable } from './components/LibrariesTable';
import { LibraryCard } from './components/LibraryCard';
import { LibraryCards } from './components/LibraryCards';
import { LibraryCreateModal, LibraryCreateModalRef } from './components/LibraryCreateModal';
import { LibraryDeleteModal, LibraryDeleteModalRef } from './components/LibraryDeleteModal';
import { LibraryList } from './components/LibraryList';
import { LibraryRenameModal, LibraryRenameModalRef } from './components/LibraryRenameModal';
import { MobileDeleteLibraryDrawer } from './components/Mobile/MobileDeleteLibraryDrawer';
import { MobileEditLibraryModal } from './components/Mobile/MobileEditLibraryModal';
import { MobileLibrariesOverlay } from './components/Mobile/MobileLibrariesOverlay';
import { MobileLibraryContextDrawer } from './components/Mobile/MobileLibraryContextDrawer';

const LibrariesContent = observer(() => {
  const [activeLibraryId, setActiveLibraryId] = useState<string | null>(null);
  const [activeLibraryName, setActiveLibraryName] = useState<string>('');
  const renameModalRef = useRef<LibraryRenameModalRef | null>(null);
  const deleteModalRef = useRef<LibraryDeleteModalRef | null>(null);
  const createModalRef = useRef<LibraryCreateModalRef>(null);
  const libraryManager = useLibraryManager();
  const uiPreferences = useUIPreferences();
  // Drives the pin store (hydrate + prune) without reading pin state: the pin glyphs
  // subscribe to `libraryFavorites` themselves, in `LibraryGridCard` and the table's
  // `PinCell`.
  useLibraryFavoritesSync();
  const isMobile = useIsMobile();

  // Mobile drawer/modal state
  const [contextDrawerOpen, setContextDrawerOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDrawerOpen, setDeleteDrawerOpen] = useState(false);
  const [previewThumbnailUrl, setPreviewThumbnailUrl] = useState<string | undefined>(undefined);
  const [isDeletePressing, setIsDeletePressing] = useState(false);

  // Stable identity: the table puts this in the `useMemo` dependency list for its
  // column definitions, and a fresh function each render makes MUI rebuild its
  // column state — discarding the user's column resizes and reordering. The grid needs
  // it stable too, so `LibraryGridCard`'s memo can skip siblings on a reorder. The pin
  // state is deliberately never passed to either surface for the same reason.
  const handleLibrarySelect = useCallback((id: string, name: string) => {
    setActiveLibraryId(id);
    setActiveLibraryName(name);
  }, []);

  const handleNewLibrary = useCallback(() => {
    trackingStore.newLibraryButtonClicked();
    createModalRef.current?.open();
  }, []);

  // Add-media target for the empty-library card action.
  //
  // Two constraints pull in opposite directions, and satisfying only one of them is a
  // bug either way:
  //
  //  - The modal reads its destination from a *prop*, so on the first click for a
  //    library the id must be committed before `open()` runs. Calling both in one
  //    handler would open against the previous library (or, from null, the account
  //    default). Hence the effect.
  //  - But the modal stays mounted after the user closes it, and `setState` with an
  //    unchanged value is a no-op — React bails out, the effect's deps never change,
  //    and `open()` never fires again. So re-clicking the *same* library silently did
  //    nothing the second time.
  //
  // So: a new library goes through state (effect opens it once the prop has
  // committed), while the already-mounted one is opened directly. The id is mirrored
  // into a ref purely so the handler can make that decision without listing the state
  // as a dependency and changing identity on every pick.
  const [addMediaLibraryId, setAddMediaLibraryId] = useState<string | null>(null);
  const addMediaLibraryIdRef = useRef<string | null>(null);
  addMediaLibraryIdRef.current = addMediaLibraryId;
  const addMediaModalRef = useRef<AddMediaModalRef>(null);

  const handleAddMedia = useCallback((libraryId: string) => {
    if (addMediaLibraryIdRef.current === libraryId) {
      addMediaModalRef.current?.open();
      return;
    }
    setAddMediaLibraryId(libraryId);
  }, []);

  useEffect(() => {
    if (addMediaLibraryId) addMediaModalRef.current?.open();
  }, [addMediaLibraryId]);

  // Memoised for the same reason as `handleLibrarySelect`: it reaches the grid cards as a
  // prop, and a fresh identity each render defeats `LibraryGridCard`'s memo. Setters only,
  // so `[]` deps.
  const handleMobileMenuClick = useCallback(
    (id: string, name: string, thumbnailBlobUrl?: string) => {
      setActiveLibraryId(id);
      setActiveLibraryName(name);
      setPreviewThumbnailUrl(thumbnailBlobUrl);
      setContextDrawerOpen(true);
    },
    [],
  );

  const handleMobileRename = () => {
    setContextDrawerOpen(false);
    setEditModalOpen(true);
  };

  const handleMobileDelete = () => {
    setDeleteDrawerOpen(true);
  };

  const handleMobileDeleteComplete = () => {
    setDeleteDrawerOpen(false);
    setContextDrawerOpen(false);
    setEditModalOpen(false);
  };

  const library = activeLibraryId
    ? libraryManager.libraries.find((l) => l.libraryId === activeLibraryId)
    : null;

  const disableDelete = library?.isDefault || library?.isDemo || library?.isReadOnly;

  // The meta lines for the preview card that floats above the mobile context drawer. Built with
  // the grid's own helper so the preview reads exactly like the row it was summoned from.
  const previewMeta = library ? formatLibraryCardMeta(library) : null;

  // Gating (loading / filtered-to-nothing) and dispatch (which view) read as a
  // sequence of early returns rather than a four-deep nested ternary. The gates live
  // here rather than inside a view so both views share them, and so the create modal
  // below stays mounted even when they short-circuit.
  //
  // The filter gate reads `sortedAndFilteredLibraries.length`, which subscribes this
  // component to the whole computed — so a pin toggle re-renders this page even though it
  // no longer reads pin state. That's fine and not worth engineering around: it renders the
  // nav, one view and some already-mounted modals, and the views below are memoised where
  // it counts.
  const renderLibraries = () => {
    // Waiting on pins as well as stats avoids a visible reshuffle: stats arrive inline on
    // `/library`, so `areLibraryStatsLoaded` flips while the pinned ids are still coming
    // back from user settings — first paint would show the plain sort, then jump. A failed
    // settings read can't wedge the page: `hydrated` is set in a `finally`.
    if (!libraryManager.areLibraryStatsLoaded || !libraryFavorites.hydrated) return null;

    // Not over the table: its search line is the filter, and it shows its own empty state, so
    // swapping it out would take the search box away mid-typing.
    const showsTable = uiPreferences.librariesViewMode === 'list' && !isMobile;
    if (
      libraryManager.sortedAndFilteredLibraries.length === 0 &&
      libraryManager.hasOmniFilter &&
      !showsTable
    ) {
      return (
        <NoResultForFilter
          title="No libraries match your current filter"
          onClearFilters={() => libraryManager.setOmniFilter(null)}
        />
      );
    }

    if (uiPreferences.librariesViewMode === 'grid') {
      return (
        <LibraryCards
          onLibrarySelect={handleLibrarySelect}
          renameModalRef={renameModalRef}
          deleteModalRef={deleteModalRef}
          onMobileMenuClick={isMobile ? handleMobileMenuClick : undefined}
          hiddenLibraryId={contextDrawerOpen ? activeLibraryId : null}
          onNewLibrary={handleNewLibrary}
          onAddMedia={handleAddMedia}
        />
      );
    }

    // List view. Mobile keeps hand-rolled rows since a data grid is unusable at
    // 360px — the same split as the Library page's table / MobileMediaTableList pair.
    if (isMobile) {
      return (
        <LibraryList
          onMobileMenuClick={handleMobileMenuClick}
          hiddenLibraryId={contextDrawerOpen ? activeLibraryId : null}
          onNewLibrary={handleNewLibrary}
        />
      );
    }

    return (
      <LibrariesTable
        onLibrarySelect={handleLibrarySelect}
        renameModalRef={renameModalRef}
        deleteModalRef={deleteModalRef}
        onAddMedia={handleAddMedia}
      />
    );
  };

  return (
    <div className={css.container}>
      {isMobile && <MobileLibrariesOverlay />}
      <LibrariesNav onNewLibrary={handleNewLibrary} />
      {renderLibraries()}
      <LibraryCreateModal ref={createModalRef} />

      {/* One add-media modal for the whole page, opened by any library's Add media
          action with that library as the destination. AddMediaModal mounts its own
          LibraryFolderProvider / UploadURLProvider, so it needs nothing from here
          beyond the id.
          No `key`: the modal already clears `selectedDestination` on close
          (AddMediaModal.tsx), so a reopen always falls back to whichever library was
          just pressed. Remounting it to force that would be belt-on-belt. */}
      {addMediaLibraryId && (
        <AddMediaModal ref={addMediaModalRef} destination={{ libraryId: addMediaLibraryId }} />
      )}

      {/* Desktop modals */}
      {!isMobile && activeLibraryId && activeLibraryName && library && (
        <>
          <LibraryFolderProvider arguments={[library]}>
            <LibraryRenameModal
              ref={renameModalRef}
              libraryId={activeLibraryId}
              currentName={activeLibraryName}
            />
          </LibraryFolderProvider>
          <LibraryDeleteModal
            ref={deleteModalRef}
            libraryId={activeLibraryId}
            currentName={activeLibraryName}
          />
        </>
      )}

      {/* Mobile drawers/modals */}
      {isMobile && activeLibraryId && activeLibraryName && library && (
        <>
          {/* Preview card above drawer backdrop. The backdrop is a child of the
              drawer's root, so it shares the drawer's 1400 stacking context — the
              card can only be wholly below or wholly above the drawer, never
              between scrim and sheet. Hence aboveMobileDrawer (1450). */}
          {contextDrawerOpen &&
            createPortal(
              <div
                className={classNames(css.drawerPreviewCard, {
                  [css.burning]: isDeletePressing,
                })}
                style={{ zIndex: Z_INDEX.aboveMobileDrawer }}
              >
                <LibraryCard
                  id={activeLibraryId}
                  name={activeLibraryName}
                  mediaCount={previewMeta?.mediaCount ?? undefined}
                  folderCount={previewMeta?.folderCount}
                  ownerLine={previewMeta?.owner ?? undefined}
                  library={library}
                  hideOptions
                  noLink
                  thumbnail={previewThumbnailUrl}
                />
              </div>,
              document.body,
            )}
          <MobileLibraryContextDrawer
            surface="libraries"
            open={contextDrawerOpen}
            onClose={() => setContextDrawerOpen(false)}
            onOpen={() => setContextDrawerOpen(true)}
            libraryId={activeLibraryId}
            disableDelete={disableDelete}
            onRename={handleMobileRename}
            onDelete={handleMobileDelete}
            // Grid cards carry the verb rail (revealed at the viewport centre line), so
            // the drawer drops the verbs there; the mobile list row has no rail, so it
            // keeps them.
            hasVerbRail={uiPreferences.librariesViewMode === 'grid'}
            // This page drives the pin store (and gates first paint on it), so it's the host
            // that can honestly offer pinning — the in-library header cannot.
            showPin
          />
          <LibraryFolderProvider arguments={[library]}>
            <MobileEditLibraryModal
              open={editModalOpen}
              onClose={() => setEditModalOpen(false)}
              libraryId={activeLibraryId}
              currentName={activeLibraryName}
            />
          </LibraryFolderProvider>
          <MobileDeleteLibraryDrawer
            open={deleteDrawerOpen}
            onClose={() => setDeleteDrawerOpen(false)}
            onOpen={() => setDeleteDrawerOpen(true)}
            libraryId={activeLibraryId}
            currentName={activeLibraryName}
            onDeleted={handleMobileDeleteComplete}
            onPressingChange={setIsDeletePressing}
          />
        </>
      )}
    </div>
  );
});

export const LibrariesPage = observer(function LibrariesPage() {
  return (
    <AppPage header="Libraries">
      <LibrariesContent />
    </AppPage>
  );
});
